import React from 'react';
import { RiskTolerance } from '@trp-individual-investor-adv-svc/ii-prosp-experiences';
import '@trp-beacon-ui-kit/foundation/style-tokens/css/variables.css';
import '@trp-beacon-ui-kit/foundation/fonts/fonts.css';

import api from '../../../actions/EndPoints';
import axiosInstance from '../../../actions/AxiosInstance';
import { closeModal, getCookie, setLastRequestTime, showModal } from '../../../Utils';
import { dispatchOverlayLoad } from '../../shared/Analytics';
import {
    GENERAL_HUB_ERROR_MODAL,
    HUB_GENERAL_ERROR_OVERLAY,
    PROSP_MODAL_ADD_ACCOUNTS,
    PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE,
} from '../../../Constants';
import './index.css';

const ATTRIBUTE_NAMES = ['rtqTimeHorizon', 'rtqDrawdown', 'rtqPastInvesting', 'rtqFutureInvesting'];

const transformGetResponse = (apiResponse) => {
    if (!apiResponse?.attributes) {
        return {};
    }
    const payload = {};
    apiResponse.attributes.forEach((attr) => {
        if (ATTRIBUTE_NAMES.includes(attr.attributeName)) {
            payload[attr.attributeName] = attr.attributeValue?.[0] ?? '';
        }
    });
    return payload;
};

const RTQModal = ({ state, dispatch }) => {
    const PARTICIPATION = 'Advice Invest Participation Level';
    const uberId = getCookie('cl-uber');
    const getRTQParticipation = (preferences) => {
        if (preferences) {
            return (
                preferences?.filter((pref) => pref.preference === PARTICIPATION)[0]
                    ?.preferenceValue ?? ''
            );
        }
        return '';
    };
    const [rtqModalData, setRtqModalData] = React.useState({
        ...transformGetResponse(state.rtqData),
        participation: {
            id: getRTQParticipation(state?.clientPreferences?.preferences),
        },
    });
    const [isLoading, setIsLoading] = React.useState(false);
    const [isClientPrefUpdated, setClientPrefUpdated] = React.useState(false);

    React.useEffect(() => {
        setRtqModalData({
            ...transformGetResponse(state.rtqData),
            participation: getRTQParticipation(state?.clientPreferences?.preferences),
        });
    }, [state.rtqData, state.clientPreferences]);

    async function updateClientPreferences() {
        setClientPrefUpdated(true);
        setIsLoading(true);
        const headerPayload = {
            preferences: [
                {
                    preference: PARTICIPATION,
                    preferenceValue: rtqModalData.participation,
                },
            ],
        };

        try {
            await axiosInstance.put(api.clientPreferencesAPI, headerPayload, {
                headers: {
                    apikey: `${state.apiKey}`,
                },
            });
            setLastRequestTime();
        } catch (error) {
            const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
            showErrorModalPromise
                .then(() => console.log(error))
                .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                .then(() => closeModal(PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE))
                .then(() => setClientPrefUpdated(false))
                .then(() => setLastRequestTime());
            throw error;
        }
    }

    async function postRtq() {
        setIsLoading(true);
        const now = new Date().toISOString();
        const getPayload = {
            uberId,
            source: 'ADV_HH_SVC',
            updatedBy: 'ADV_HH_SVC',
            attributes: ATTRIBUTE_NAMES
                .filter((name) => rtqModalData[name])
                .map((attributeName) => ({
                    attributeName,
                    attributeValue: [rtqModalData[attributeName]],
                    answeredAt: now,
                })),
        };

        try {
            const postCall = await axiosInstance.post(api.rtqAPI, getPayload, {
                headers: {
                    apikey: `${state.apiKey}`,
                },
            });

            if (postCall) {
                dispatch({
                    type: 'PROSP_COMPL_SEC',
                    lastProspSection: 'rtq',
                });
                dispatch({
                    type: 'PROSP_STATUSES',
                    prospStatuses: ['NEWP', 'KYCC', 'GLSC', 'RTQC'],
                });
                setIsLoading(false);
                setLastRequestTime();
            }
        } catch (error) {
            const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
            showErrorModalPromise
                .then(() => console.log(error))
                .then(() => closeModal(PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE))
                .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                .then(() => setIsLoading(false));
            throw error;
        }
    }

    const openNextModal = () => {
        showModal(PROSP_MODAL_ADD_ACCOUNTS);
    };

    const finalStep = () => {
        closeModal(PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE);
        openNextModal();
    };

    return (
        <RiskTolerance
            payload={rtqModalData}
            saveData={postRtq}
            saveClientPreferences={updateClientPreferences}
            isClientPrefUpdated={isClientPrefUpdated}
            setClientPrefUpdated={setClientPrefUpdated}
            setPayload={setRtqModalData}
            finalStep={finalStep}
            isLoading={isLoading}
            setLoading={setIsLoading}
        />
    );
};

export default RTQModal;
